import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Phone, Mail, Send, Check, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { CONTACT } from "@/constants/testIds";
import { SITE, MEDIA } from "@/lib/site";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = [
  "Mariage",
  "Corporate",
  "Festival",
  "Sport",
  "Anniversaire / Privé",
  "Demande en mariage",
  "Autre",
];

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    event_type: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.name || !form.email || !form.phone || !form.event_type || !form.message) {
      toast.error("Merci de compléter tous les champs.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Valérie vous recontactera dans un délai de 24 heures.");
      setForm({ name: "", email: "", phone: "", event_type: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Une erreur est survenue. Merci d'appeler le +33 6 66 96 15 35.";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid={CONTACT.root}
      className="relative py-28 md:py-32 overflow-hidden"
    >
      <div className="absolute inset-0">
        <img src={MEDIA.particles} alt="" className="h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#030305] via-[#030305]/90 to-[#030305]" />
      </div>

      <div className="relative mx-auto max-w-7xl px-6 grid gap-12 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-5">
          <div className="inline-flex items-center gap-2 rounded-full glass px-4 py-2 text-xs uppercase tracking-[0.2em] text-white/70">
            Contact · Devis 24h
          </div>
          <h2 className="font-display mt-6 text-4xl md:text-5xl lg:text-6xl font-light tracking-tight">
            Parlons de votre <span className="italic font-semibold text-[#007AFF]">ciel</span>.
          </h2>
          <p className="mt-6 text-white/65 leading-relaxed max-w-md">
            Décrivez votre événement — nous revenons vers vous sous 24h avec une
            proposition créative et un devis détaillé.
          </p>

          {/* Contact person card */}
          <div className="mt-8 flex items-center gap-4 rounded-2xl glass-strong p-4">
            <img
              src={SITE.contactPersonPhoto}
              alt={SITE.contactPersonName}
              className="h-16 w-16 rounded-full object-cover ring-2 ring-[#007AFF]/40"
            />
            <div>
              <div className="font-display text-lg font-semibold">{SITE.contactPersonName}</div>
              <div className="text-xs text-white/55">{SITE.contactPersonRole}</div>
              <div className="mt-1 inline-flex items-center gap-2 text-xs text-[#007AFF]">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#007AFF] animate-pulse" />
                Disponible — réponse sous 24h
              </div>
            </div>
          </div>

          <div className="mt-6 space-y-4">
            <a
              data-testid={CONTACT.callBtn}
              href={SITE.phoneLink}
              className="flex items-center gap-4 rounded-2xl glass-strong p-5 hover:bg-white/10 transition group"
            >
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#007AFF] text-white">
                <Phone size={18} />
              </span>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-white/50">Téléphone direct</div>
                <div className="font-display text-xl font-semibold">{SITE.phoneDisplay}</div>
              </div>
            </a>
            <a
              href={SITE.emailLink}
              className="flex items-center gap-4 rounded-2xl glass-strong p-5 hover:bg-white/10 transition group"
            >
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white">
                <Mail size={18} />
              </span>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-white/50">Email</div>
                <div className="font-display text-lg font-semibold">{SITE.emailDisplay}</div>
              </div>
            </a>
            <a
              href={SITE.addressMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 rounded-2xl glass-strong p-5 hover:bg-white/10 transition group"
            >
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white">
                <MapPin size={18} />
              </span>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-white/50">Adresse</div>
                <div className="font-display text-base font-semibold leading-tight mt-0.5">
                  66 rue du Général de Gaulle<br />95880 Enghien-les-Bains
                </div>
              </div>
            </a>
          </div>
        </div>

        <motion.form
          data-testid={CONTACT.form}
          onSubmit={submit}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-7 rounded-3xl glass-strong p-8 md:p-10"
        >
          {sent && (
            <div
              data-testid={CONTACT.success}
              className="mb-6 flex items-start gap-3 rounded-2xl border border-[#007AFF]/40 bg-[#007AFF]/10 p-4 text-sm"
            >
              <Check className="text-[#007AFF] mt-0.5" size={18} />
              <div>
                <div className="font-semibold">Message reçu ✦</div>
                <div className="text-white/70 mt-1">
                  Valérie vous recontactera dans un délai de 24 heures.<br />
                  Pour toute demande urgente, vous pouvez nous joindre directement au {SITE.phoneDisplay}.
                </div>
              </div>
            </div>
          )}

          <div className="grid gap-5 md:grid-cols-2">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/50">Nom complet *</label>
              <input
                data-testid={CONTACT.name}
                value={form.name}
                onChange={update("name")}
                placeholder="Jean Dupont"
                className="dark-input mt-2 w-full rounded-xl px-4 py-3.5 text-sm"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/50">Email *</label>
              <input
                data-testid={CONTACT.email}
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="jean@exemple.fr"
                className="dark-input mt-2 w-full rounded-xl px-4 py-3.5 text-sm"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/50">Téléphone *</label>
              <input
                data-testid={CONTACT.phone}
                value={form.phone}
                onChange={update("phone")}
                placeholder="+33 6 12 34 56 78"
                className="dark-input mt-2 w-full rounded-xl px-4 py-3.5 text-sm"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/50">Type d'événement *</label>
              <Select
                value={form.event_type}
                onValueChange={(v) => setForm((f) => ({ ...f, event_type: v }))}
              >
                <SelectTrigger
                  data-testid={CONTACT.eventTypeTrigger}
                  className="dark-input mt-2 w-full rounded-xl px-4 py-3.5 text-sm h-auto bg-white/[0.03] border-white/10 text-white hover:bg-white/[0.05]"
                >
                  <SelectValue placeholder="Sélectionnez…" />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0c] text-white border-white/10">
                  {EVENT_TYPES.map((t) => (
                    <SelectItem
                      key={t}
                      value={t}
                      data-testid={`contact-option-${t.toLowerCase().replace(/\s+/g, "-")}`}
                      className="focus:bg-[#007AFF]/20 focus:text-white"
                    >
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Hidden field for testing agent fallback */}
              <input type="hidden" data-testid={CONTACT.eventType} value={form.event_type} readOnly />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-[0.2em] text-white/50">Votre projet *</label>
              <textarea
                data-testid={CONTACT.message}
                value={form.message}
                onChange={update("message")}
                rows={5}
                placeholder="Date, lieu, nombre d'invités, vision artistique…"
                className="dark-input mt-2 w-full rounded-xl px-4 py-3.5 text-sm resize-none"
              />
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-white/40">
              Vos données restent confidentielles. Aucune diffusion à des tiers.
            </p>
            <button
              data-testid={CONTACT.submit}
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-3 rounded-full bg-[#007AFF] px-7 py-3.5 text-sm font-semibold text-white hover:shadow-[0_0_32px_rgba(0,122,255,0.55)] transition disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Envoi en cours…
                </>
              ) : (
                <>
                  Envoyer la demande <Send size={16} />
                </>
              )}
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
};

export default Contact;
